import React, { useMemo, useState } from 'react';
import { Collapse, Grid, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import {
  AccountData,
  AccountLocationData,
  DateAvailabilityOut,
  Language,
  OrderPageData,
  TimeSlotResponseOut,
} from '../../../../api';
import { DayType, hasTicketCounts, isTimeSlotRunningOut, resolveDayType } from '../DayType';
import { isTimeSlotBookable } from '../utils/orderCutoffUtils';
import { hasAnyWidgetCustomTimeDetail, resolveWidgetCustomDetails } from '../utils/customTimeDetails';
import { OrderPageTypography } from '../../../themedComponents/OrderPageTypography';
import { OrderPageDivider } from '../../../themedComponents/OrderPageDivider';
import { getLocationNameById } from '../../../components/MultiLocationDetails';
import { useLanguage } from '../../../../utils/GeneralUtils';
import CourseSessionsList from './CourseSessionsList';

export type CondensedOption = {
  date: string;
  timeSlot: TimeSlotResponseOut;
  dayType: DayType;
  isBookable: boolean;
};

type CondensedOptionsListProps = {
  availableDates: DateAvailabilityOut[];
  availableDatesDict: { [date: string]: DateAvailabilityOut };
  orderPage: OrderPageData;
  account?: AccountData;
  selectedDate?: string | null;
  selectedTime?: number | null;
  onSelectOption: (option: CondensedOption) => void;
  locations: AccountLocationData[];
  shouldShowLocation: boolean;
  duration: number;
  cardDateFormat: string;
  timeFormat: string;
  troubleshoot?: boolean;
  // pre-open the first card's session details (used when the page has a single course option)
  defaultExpanded?: boolean;
};

const optionKey = (option: CondensedOption) => `${option.date}-${option.timeSlot.seconds}`;

export const CondensedOptionsList = ({
  availableDates,
  availableDatesDict,
  orderPage,
  account,
  selectedDate,
  selectedTime,
  onSelectOption,
  locations,
  shouldShowLocation,
  duration,
  cardDateFormat,
  timeFormat,
  troubleshoot,
  defaultExpanded,
}: CondensedOptionsListProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();

  const hasCustomTimeDetailOnPage = useMemo(() => hasAnyWidgetCustomTimeDetail(availableDates), [availableDates]);
  // hebrew weekdays read as "יום רביעי", not a bare "רביעי" - same prefix the chip grid uses
  const dateFormat = language === Language.HE ? `[יום] ${cardDateFormat}` : cardDateFormat;
  const waitingListEnabled = !!orderPage.waiting_list?.enabled;

  const options: CondensedOption[] = useMemo(() => {
    const flattened: CondensedOption[] = [];
    availableDates.forEach((availableDate) => {
      const dayType = resolveDayType({
        date: moment(availableDate.date),
        availableDatesDict,
        orderPage,
        account,
      });
      // same filter the chip grid applies - dates with nothing bookable never render
      if (dayType === DayType.NOT_AVAILABLE_DAY) {
        return;
      }
      availableDate.times.forEach((timeSlot) => {
        flattened.push({
          date: availableDate.date,
          timeSlot,
          dayType,
          isBookable:
            !timeSlot.isDisabled && isTimeSlotBookable(timeSlot.seconds, availableDate.date, orderPage, account),
        });
      });
    });
    return flattened.sort((a, b) =>
      a.date === b.date ? a.timeSlot.seconds - b.timeSlot.seconds : a.date.localeCompare(b.date),
    );
  }, [availableDates, availableDatesDict, orderPage, account]);

  const [expandedKeys, setExpandedKeys] = useState<string[]>(() =>
    defaultExpanded && options.length > 0 ? [optionKey(options[0])] : [],
  );

  const formatTimeRange = (seconds: number) =>
    `${moment.unix(seconds).tz('utc').format(timeFormat)} – ${moment
      .unix(seconds)
      .tz('utc')
      .add(duration, 'seconds')
      .format(timeFormat)}`;

  const renderBadge = (option: CondensedOption) => {
    if (!option.isBookable) {
      return (
        <OrderPageTypography variant="body2" style={{ color: theme.customTheme.palette.railFill }}>
          {t('calendarLegend.soldOut')}
        </OrderPageTypography>
      );
    }
    // ticketed slots are judged individually - a 2-of-10 slot must warn even when the rest of that day
    // is wide open. Private slots carry no counts, so they keep the day-level signal.
    const isRunningOut = hasTicketCounts(option.timeSlot)
      ? isTimeSlotRunningOut(option.timeSlot)
      : option.dayType === DayType.RUNNING_OUT_DAY;
    if (!isRunningOut) {
      return null;
    }
    return (
      <OrderPageTypography variant="body2" color="warning">
        {t('order_page.condensed.runningOut')}
      </OrderPageTypography>
    );
  };

  return (
    <Grid container flexDirection="column" gap={1.5} mt={2}>
      {options.map((option) => {
        const key = optionKey(option);
        const { timeSlot } = option;
        const isSelected = selectedDate === option.date && selectedTime === timeSlot.seconds;
        const isClickable = option.isBookable || troubleshoot || waitingListEnabled;
        const { courseSessions } = timeSlot;
        const isCourse = !!courseSessions && courseSessions.length > 0;
        const isExpanded = expandedKeys.includes(key);
        const lastSession = isCourse ? courseSessions[courseSessions.length - 1] : undefined;
        const customDetailLabel = resolveWidgetCustomDetails(timeSlot, hasCustomTimeDetailOnPage)?.find(
          (detail) => detail.show_in_widget,
        )?.label[language];
        const formattedDate = moment(option.date).format(dateFormat);

        return (
          <Grid
            container
            flexDirection="column"
            key={key}
            role="button"
            tabIndex={isClickable ? 0 : -1}
            aria-pressed={isSelected}
            onClick={() => isClickable && onSelectOption(option)}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onSelectOption(option);
              }
            }}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: isSelected
                ? `3px solid ${theme.customTheme.palette.primary}`
                : `1px solid ${theme.customTheme.palette.border}`,
              // a translucent tint of the primary colour, so it composites over whatever the account's
              // background is. toggleButton.selectedBg is always a near-white fill, which reads as a
              // blank block on dark themes where the card text is light.
              backgroundColor: isSelected ? `${theme.customTheme.palette.primary}20` : undefined,
              cursor: isClickable ? 'pointer' : 'default',
              opacity: isClickable ? 1 : 0.6,
              outline:
                troubleshoot && !option.isBookable ? `2px solid ${theme.customTheme.palette.warning}` : undefined,
              '&:hover': isClickable ? { borderColor: theme.customTheme.palette.primary } : undefined,
            }}>
            <OrderPageTypography
              variant="h3"
              bold
              style={{ textDecoration: option.isBookable ? undefined : 'line-through' }}>
              {isCourse ? t('order_page.condensed.startsOn', { date: formattedDate }) : formattedDate}
            </OrderPageTypography>

            <Grid container alignItems="baseline" gap={1} mt={0.5}>
              <Grid dir="ltr">
                <OrderPageTypography variant="h6">{formatTimeRange(timeSlot.seconds)}</OrderPageTypography>
              </Grid>
              {lastSession && (
                <OrderPageTypography variant="body2" color="border">
                  {t('order_page.condensed.untilDate', {
                    date: moment(lastSession.date).format(dateFormat),
                  })}
                </OrderPageTypography>
              )}
            </Grid>

            {customDetailLabel && (
              <OrderPageTypography variant="body2" color="border">
                {customDetailLabel}
              </OrderPageTypography>
            )}

            {shouldShowLocation && timeSlot.location_id && (
              <OrderPageTypography variant="body2" color="border">
                {getLocationNameById(timeSlot.location_id, locations, language)}
              </OrderPageTypography>
            )}

            {renderBadge(option)}

            {isCourse && (
              <>
                <Grid
                  container
                  alignItems="center"
                  gap={0.5}
                  mt={1}
                  width="auto"
                  role="button"
                  aria-expanded={isExpanded}
                  onClick={(event: React.MouseEvent) => {
                    // expanding is a peek at the schedule, it must not pick the option
                    event.stopPropagation();
                    setExpandedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
                  }}
                  sx={{ cursor: 'pointer' }}>
                  <OrderPageTypography variant="body2" style={{ textDecoration: 'underline' }}>
                    {t('order_page.condensed.sessionDetails')}
                  </OrderPageTypography>
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Grid>
                <Collapse in={isExpanded} unmountOnExit>
                  <Grid mt={1}>
                    <OrderPageDivider />
                  </Grid>
                  <CourseSessionsList
                    courseSessions={courseSessions}
                    longDateFormat={dateFormat}
                    timeFormat={timeFormat}
                    duration={duration}
                  />
                </Collapse>
              </>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};
